import { useMemo, useState } from 'react';
import { CheckCircle, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useEnterprisePartnerAuditLog } from '@/hooks/useEnterprisePartners';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface PartnerAuditLogProps {
  partnerId: string;
}

interface AuditRow {
  id: string;
  partner_id: string;
  identity_id: string | null;
  buildit_user_id: string | null;
  event_type: string;
  status: string;
  error_code: string | null;
  nonce: string | null;
  ip_address: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

type StatusFilter = 'all' | 'success' | 'failure';
type BucketFilter = 'all' | 'token' | 'partner' | 'user' | 'workspace' | 'identity';

const EVENT_TYPE_LABELS: Record<string, string> = {
  token_verified: 'Token verified',
  token_rejected_format: 'Token rejected (format)',
  token_rejected_unknown_partner: 'Token rejected (unknown partner)',
  token_rejected_partner_disabled: 'Token rejected (partner disabled)',
  token_rejected_signature: 'Token rejected (bad signature)',
  token_rejected_expired: 'Token rejected (expired)',
  token_rejected_replay: 'Token rejected (replay)',
  token_rejected_identity_revoked: 'Token rejected (identity revoked)',
  token_rejected_shared_workspace_deleted: 'Token rejected (workspace deleted)',
  token_rejected_rate_limited: 'Token rejected (rate limited)',
  user_provisioned_new: 'User provisioned (new)',
  user_linked_existing: 'User linked (existing)',
  workspace_provisioned: 'Workspace provisioned',
  identity_deprovisioned: 'Identity deprovisioned',
  partner_created: 'Partner created',
  partner_secret_rotated: 'Partner secret rotated',
  partner_admin_added: 'Partner admin added',
  partner_admin_removed: 'Partner admin removed',
  partner_status_changed: 'Partner status changed',
  generate_link_failed: 'GenerateLink failed',
  finalize_failed: 'Finalize failed',
  session_mint_failed: 'Session mint failed',
  internal_error: 'Internal error',
};

function formatEventType(t: string): string {
  return (
    EVENT_TYPE_LABELS[t] ??
    t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'success', label: 'Success' },
  { value: 'failure', label: 'Failure' },
];

const BUCKET_OPTIONS: { value: BucketFilter; label: string; prefix?: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'token', label: 'Token', prefix: 'token_' },
  { value: 'partner', label: 'Partner', prefix: 'partner_' },
  { value: 'user', label: 'User', prefix: 'user_' },
  { value: 'workspace', label: 'Workspace', prefix: 'workspace_' },
  { value: 'identity', label: 'Identity', prefix: 'identity_' },
];

function bucketMatches(eventType: string, bucket: BucketFilter): boolean {
  if (bucket === 'all') return true;
  const opt = BUCKET_OPTIONS.find((b) => b.value === bucket);
  if (!opt?.prefix) return true;
  return eventType.startsWith(opt.prefix);
}

interface DetailEntries {
  metadata: Record<string, unknown> | null;
  nonce: string | null;
  ip_address: string | null;
  user_agent: string | null;
  identity_id: string | null;
  buildit_user_id: string | null;
}

function buildDetails(row: AuditRow): DetailEntries {
  return {
    metadata: row.metadata,
    nonce: row.nonce,
    ip_address: row.ip_address,
    user_agent: row.user_agent,
    identity_id: row.identity_id,
    buildit_user_id: row.buildit_user_id,
  };
}

function hasAnyDetails(d: DetailEntries): boolean {
  if (d.metadata && Object.keys(d.metadata).length > 0) return true;
  if (d.nonce) return true;
  if (d.ip_address) return true;
  if (d.user_agent) return true;
  if (d.identity_id) return true;
  if (d.buildit_user_id) return true;
  return false;
}

function detailsForDisplay(d: DetailEntries): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (d.metadata && Object.keys(d.metadata).length > 0) out.metadata = d.metadata;
  if (d.nonce) out.nonce = d.nonce;
  if (d.ip_address) out.ip_address = d.ip_address;
  if (d.user_agent) out.user_agent = d.user_agent;
  if (d.identity_id) out.identity_id = d.identity_id;
  if (d.buildit_user_id) out.buildit_user_id = d.buildit_user_id;
  return out;
}

export function PartnerAuditLog({ partnerId }: PartnerAuditLogProps): JSX.Element {
  const { data, isLoading, error } = useEnterprisePartnerAuditLog(partnerId);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [bucketFilter, setBucketFilter] = useState<BucketFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const rows = (data ?? []) as AuditRow[];

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!bucketMatches(r.event_type, bucketFilter)) return false;
      return true;
    });
  }, [rows, statusFilter, bucketFilter]);

  const clearFilters = () => {
    setStatusFilter('all');
    setBucketFilter('all');
  };

  // TODO: server-side filtering — currently client-filtering over the most
  // recent 100 rows returned by enterprise_sso_audit_log_query. Add filter
  // params to the RPC when row volume becomes a concern.
  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              Status:
            </span>
            {STATUS_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                size="sm"
                variant={statusFilter === opt.value ? 'default' : 'outline'}
                className="h-7 px-3 text-xs"
                onClick={() => setStatusFilter(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              Type:
            </span>
            {BUCKET_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                size="sm"
                variant={bucketFilter === opt.value ? 'default' : 'outline'}
                className="h-7 px-3 text-xs"
                onClick={() => setBucketFilter(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        {isLoading && (
          <div className="space-y-2">
            <div className="h-12 animate-pulse rounded bg-muted" />
            <div className="h-12 animate-pulse rounded bg-muted" />
            <div className="h-12 animate-pulse rounded bg-muted" />
          </div>
        )}

        {error && !isLoading && (
          <div className="rounded border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            Failed to load audit log:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        )}

        {!isLoading && !error && rows.length === 0 && (
          <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
            No audit events for this partner yet.
          </div>
        )}

        {!isLoading && !error && rows.length > 0 && filtered.length === 0 && (
          <div className="flex flex-col items-center gap-3 rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
            <p>No events matching the current filters.</p>
            <Button size="sm" variant="outline" onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <ul className="divide-y rounded border">
            {filtered.map((row) => {
              const isFailure = row.status === 'failure';
              const isExpanded = expandedId === row.id;
              const details = buildDetails(row);
              const expandable = hasAnyDetails(details);
              const created = new Date(row.created_at);

              return (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() =>
                      expandable && setExpandedId(isExpanded ? null : row.id)
                    }
                    className={cn(
                      'flex w-full items-center gap-3 px-4 py-3 text-left text-sm',
                      expandable && 'hover:bg-muted/50',
                      !expandable && 'cursor-default'
                    )}
                    aria-expanded={expandable ? isExpanded : undefined}
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                      {expandable ? (
                        isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )
                      ) : null}
                    </span>

                    {isFailure ? (
                      <XCircle
                        className="h-4 w-4 shrink-0 text-destructive"
                        aria-label="Failure"
                      />
                    ) : (
                      <CheckCircle
                        className="h-4 w-4 shrink-0 text-green-600 dark:text-green-500"
                        aria-label="Success"
                      />
                    )}

                    <span className="flex-1 truncate font-medium">
                      {formatEventType(row.event_type)}
                    </span>

                    {isFailure && row.error_code && (
                      <Badge
                        variant="destructive"
                        className="shrink-0 font-mono text-[10px]"
                      >
                        {row.error_code}
                      </Badge>
                    )}

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {formatDistanceToNow(created, { addSuffix: true })}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {format(created, 'PPpp')}
                      </TooltipContent>
                    </Tooltip>
                  </button>

                  {isExpanded && expandable && (
                    <div className="border-t bg-muted/20 px-4 py-3">
                      <pre className="max-h-64 overflow-auto rounded bg-muted p-3 text-xs">
                        {JSON.stringify(detailsForDisplay(details), null, 2)}
                      </pre>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </TooltipProvider>
  );
}
